import React, { type ReactElement, useMemo, useState } from 'react'
import { PrimaryButton } from '../../components/Button'
import FileInput from '../../components/FileInput'
import { InputLabel, OptionalLabel } from '../../components/Input'
import PageHeader from '../../components/PageHeader'
import type { Address, BusinessAddress as BusinessAddressState } from '../../state/Radom'
import { errorToast, highlightInvalidFields } from '../../util/Util'
import Tooltip from '../../components/Tooltip'
import { AddressForm } from './AddressForm'
import PdfFileUpload from './PDFFileUpload'
import { RADOM_COLORS } from '../../util/Constants'

interface IProps {
  state: BusinessAddressStateExtended
  isSubmitting: boolean
  onUpdate: (value: BusinessAddressStateExtended) => void
  onSubmit: () => void
}

type BusinessAddressStateExtended = BusinessAddressState & {
  proofOfAddressFile?: File
  proofOfAddressUrl?: string
}

const BusinessAddress = ({ state, isSubmitting, onUpdate, onSubmit }: IProps): ReactElement => {
  const isValid = useMemo(() => ({
    streetLine: state.physicalAddress?.streetLine1 !== '',
    city: state.physicalAddress?.city !== '',
    country: state.physicalAddress?.country !== '',
    proofOfAddressFile: !!state.proofOfAddressFile || !!state.proofOfAddress
  }), [state])

  const isFormValid = useMemo(() => Object.values(isValid).every((value) => value), [isValid])

  return (<div style={{ display: 'flex', flexDirection: 'column', gap: 20, fontSize: 14, paddingBottom: 40 }}>
    <div style={{ marginBottom: 20 }}>
      <PageHeader title="Tell us where you're located" subTitle="Share your business's registered and physical addresses" />
    </div>

    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <span style={{ fontSize: 20 }}>Registered address</span>
      <AddressForm state={state.registeredAddress} onUpdate={(t) => {
        onUpdate({ ...state, registeredAddress: t })
      }} />
    </div>

    <div style={{ display: 'flex', flexDirection: 'column', gap: 20, marginTop: 20 }}>
      <span style={{ fontSize: 20 }}>Operating address</span>
      <AddressForm state={state.physicalAddress} onUpdate={(t) => {
        onUpdate({ ...state, physicalAddress: t })
      }} />
    </div>

    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 10 }}>
        <InputLabel>
          Proof of address
        </InputLabel>
        <OptionalLabel>PDF</OptionalLabel>
        <Tooltip message='Upload a document proving your operating address, such as a utility bill or bank statement.' />
      </div>
      <div style={{ display: 'flex', gap: 10, flexDirection: 'column' }}>
        <div style={{
          border: `1px solid ${RADOM_COLORS.GRAY_DARK}`,
          padding: 20,
          background: RADOM_COLORS.GRAY9,
          borderRadius: 10,
          display: 'flex',
          flexDirection: 'column',
          gap: 10
        }}>
          <span>Upload one of the following issued to the applying entity:</span>
          <ul style={{ paddingLeft: 30, margin: 0 }}>
            <li><b>Utility bill</b> (less than 90 days old)</li>
            <li><b>Bank statement</b> (less than 90 days old)</li>
            <li><b>Office lease agreement</b> (may be older than 90 days)</li>
          </ul>
        </div>
        <PdfFileUpload
          file={state.proofOfAddressUrl ?? state.proofOfAddressFile}
          onFile={f => onUpdate({ ...state, proofOfAddressFile: f })}
          onRemove={() => {
            onUpdate({ ...state, proofOfAddressUrl: undefined, proofOfAddressFile: undefined })
          }} />
      </div>
    </div>

    <PrimaryButton
      disabled={isSubmitting}
      isLoading={isSubmitting}
      onClick={() => {
        if (isFormValid) {
          onSubmit()
        } else {
          highlightInvalidFields()
        }
      }}>
      Continue
    </PrimaryButton>
  </div>
  )
}

export default BusinessAddress
